import { BarChart3, Trophy } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { BAR_COLORS, PANEL_CLASS } from "./dashboardUtils";

// Stable chart config (module scope) to avoid re-creating objects each render.
const BAR_MARGIN = { top: 4, right: 24, bottom: 4, left: 12 };
const X_TICK = { fill: "#647384", fontSize: 12 };
const Y_TICK = { fill: "#122131", fontSize: 12 };
const TOOLTIP_CURSOR = { fill: "rgba(17,42,67,0.04)" };
const TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid rgba(17,42,67,0.12)", fontSize: 13 };
const BAR_RADIUS = [0, 8, 8, 0];
const formatSignups = (v) => [`${v} sign-up${v > 1 ? "s" : ""}`, "Registrations"];

export const AdviserChart = ({ data }) => (
  <div className={PANEL_CLASS} data-testid="adviser-chart">
    <div className="p-5 border-b border-[rgba(17,42,67,0.08)] flex items-center gap-2">
      <BarChart3 size={20} className="text-[#126F6A]" />
      <h2 className="text-lg font-bold text-[#122131]">Sign-ups by Adviser</h2>
    </div>
    <div className="p-5">
      {data.length === 0 ? (
        <div className="py-10 text-center text-[#647384]">
          No adviser-referred sign-ups yet. Shareable links are ready in the panel below.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(220, data.length * 42)}>
          <BarChart data={data} layout="vertical" margin={BAR_MARGIN}>
            <XAxis type="number" allowDecimals={false} tick={X_TICK} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" width={170} tick={Y_TICK} axisLine={false} tickLine={false} />
            <Tooltip
              cursor={TOOLTIP_CURSOR}
              formatter={formatSignups}
              contentStyle={TOOLTIP_STYLE}
            />
            <Bar dataKey="count" radius={BAR_RADIUS} maxBarSize={26}>
              {data.map((d, i) => (
                <Cell key={d.name} fill={BAR_COLORS[i % BAR_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  </div>
);

const RANK_COLORS = ["#F7B838", "#B9C2CC", "#D8A15A"];

const LeaderboardRow = ({ row, rank, total, topCount }) => {
  const share = total > 0 ? Math.round((row.count / total) * 100) : 0;
  const badge = rank < 3 ? RANK_COLORS[rank] : "rgba(17,42,67,0.08)";
  const badgeText = rank < 3 ? "#071524" : "#647384";
  return (
    <li
      data-testid={`leaderboard-row-${rank}`}
      className="flex items-center gap-4 rounded-xl border border-[rgba(17,42,67,0.08)] px-4 py-3 hover:bg-[#FBF8F0] transition-colors"
    >
      <span
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold"
        style={{ background: badge, color: badgeText }}
      >
        {rank + 1}
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-[#122131] truncate">{row.name}</div>
        <div className="mt-1 h-1.5 w-full rounded-full bg-[rgba(17,42,67,0.06)] overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${topCount > 0 ? (row.count / topCount) * 100 : 0}%`,
              background: "linear-gradient(90deg,#1F9188,#126F6A)",
            }}
          />
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-lg font-bold text-[#122131] leading-none">{row.count}</div>
        <div className="text-xs text-[#647384] mt-1">{share}% of total</div>
      </div>
    </li>
  );
};

export const AdviserLeaderboard = ({ rows, total }) => (
  <div className={PANEL_CLASS} data-testid="adviser-leaderboard">
    <div className="p-5 border-b border-[rgba(17,42,67,0.08)] flex items-center gap-2">
      <Trophy size={20} className="text-[#F7B838]" />
      <h2 className="text-lg font-bold text-[#122131]">Adviser Leaderboard</h2>
    </div>
    <div className="p-5">
      {rows.length === 0 ? (
        <div className="py-8 text-center text-[#647384]">No adviser-referred sign-ups yet.</div>
      ) : (
        <ol className="space-y-2">
          {rows.map((row, i) => (
            <LeaderboardRow key={row.name} row={row} rank={i} total={total} topCount={rows[0].count} />
          ))}
        </ol>
      )}
    </div>
  </div>
);
